/**
 * KIOSK 多語系（中／英／日）。
 *
 * docs/Kiosk_UI_Flow.md 規範三語；zh-TW 為主語言，三語皆完整。
 * 商品名稱等動態內容由 API 的 *_i18n 欄位提供，不在此表。
 */

export type Locale = 'zh-TW' | 'en' | 'ja';

export const LOCALES: { code: Locale; label: string }[] = [
  { code: 'zh-TW', label: '繁中' },
  { code: 'en', label: 'English' },
  { code: 'ja', label: '日本語' },
];

type Dict = Record<string, [zh: string, en: string, ja: string]>;

const STRINGS: Dict = {
  'common.back': ['返回', 'Back', '戻る'],
  'common.next': ['下一步', 'Next', '次へ'],
  'common.cancel': ['取消', 'Cancel', 'キャンセル'],
  'common.confirm': ['確認', 'Confirm', '確認'],
  'common.callStaff': ['呼叫服務人員', 'Call staff', 'スタッフを呼ぶ'],
  'common.loading': ['載入中…', 'Loading…', '読み込み中…'],
  'common.retry': ['重試', 'Retry', '再試行'],
  'common.restart': ['重新開始', 'Start over', '最初から'],
  'common.home': ['回首頁', 'Home', 'ホーム'],
  'common.errNetwork': [
    '系統暫時無法連線，請稍候重試',
    'Network unavailable, please retry',
    '接続できません。再試行してください',
  ],
  'common.errGeneric': [
    '發生問題，請重試或呼叫服務人員',
    'Something went wrong — retry or call staff',
    '問題が発生しました。スタッフをお呼びください',
  ],

  // ── 頁尾工具列（2026-08-18 設計稿）────────────────────────────────
  'footer.support': ['客服協助', 'Assistance', 'サポート'],
  'footer.supportSub': ['呼叫服務人員', 'Call for staff', 'スタッフを呼ぶ'],
  'footer.supportAcked': ['已通知，請稍候', 'Staff notified', 'お呼びしました'],
  'footer.notice': ['購票須知', 'Before you buy', 'ご購入前に'],
  'footer.noticeSub': ['詳閱購票資訊', 'Read the terms', 'ご案内を読む'],
  'footer.stepLabel': ['購票步驟', 'Purchase step', '購入ステップ'],

  // ── 購票須知彈窗 ──────────────────────────────────────────────────
  // 四段標題取自設計稿；內文業主尚未提供，故以 notice.pending 佔位。
  // 須知會被旅客當成契約依據，條文不由開發端代擬。
  'notice.title': ['購票須知', 'Before you buy', 'ご購入前のご案内'],
  'notice.checkin.title': ['報到提醒', 'Check-in', 'チェックイン'],
  'notice.change.title': ['航程異動', 'Schedule changes', '運航の変更'],
  'notice.cruise.title': ['遊艇航程', 'Yacht cruises', 'ヨットクルーズ'],
  'notice.water.title': ['水上設施', 'Water activities', 'ウォーターアクティビティ'],
  'notice.pending': ['（條文內容待提供）', '(Terms pending)', '（規約は準備中です）'],
  'notice.close': ['我知道了', 'Got it', '確認しました'],

  'attract.tagline': [
    '輕觸螢幕 開始選擇你的航程',
    'Touch to start your voyage',
    '画面にタッチして航海を始めましょう',
  ],
  'attract.mascotName': ['小獅船長', 'Captain Lion', 'ライオン船長'],
  'attract.touchStart': ['觸碰開始', 'Touch to start', 'タッチで開始'],
  'attract.touchStartSub': ['瀏覽活動與行程', 'Browse tours & activities', 'アクティビティを見る'],
  // 設計稿把「用講的試試看」改稱「語音互動」，副標是引導語（後面接省略號）
  'attract.voice': ['語音互動', 'Voice', '音声で操作'],
  'attract.voiceSub': ['說出……', 'Just say it…', '話しかけてください…'],
  // 三顆對話泡泡：示範可以說什麼。內容取自設計稿，非動態產生。
  'attract.bubbleRecommend': ['推薦今日行程', "Recommend today's tour", '今日のおすすめは？'],
  'attract.bubbleCruise': [
    '我想搭船遊安平港！',
    'I want a harbour cruise!',
    '安平港をクルーズしたい！',
  ],
  'attract.bubbleWater': ['想玩水上設施', 'Water activities', 'ウォーターアクティビティ'],
  'attract.todaySchedule': ['今日班次', "Today's sailings", '本日の便'],
  'attract.systemOk': ['系統正常 · 連線正常', 'System OK · Online', 'システム正常 · オンライン'],

  'language.title': ['選擇語言 / Choose Language / 言語を選択', 'Choose Language', '言語を選択'],

  'home.welcome': [
    '歡迎來到福爾摩沙遊艇酒店',
    'Welcome to Formosa Yacht Resort',
    'フォルモサ・ヨットリゾートへようこそ',
  ],
  'home.subtitle': [
    '請選擇您想體驗的航程或行程',
    'Choose the voyage or experience you want',
    'ご希望の航路・体験をお選びください',
  ],

  // ── 主功能四格（設計稿 p.5，2×2）─────────────────────────────────
  // 名稱與副標一字取自設計稿；此前的「我想搭船／我自己選／我已購票要報到」為舊稱。
  'menu.recommend': ['行程推薦', 'Recommended', 'おすすめ'],
  'menu.recommendSub': ['小獅船長為您安排', 'Captain Lion plans it', '船長がご提案'],
  'menu.cruise': ['搭船遊港', 'Harbour cruise', 'ハーバークルーズ'],
  'menu.cruiseSub': ['瀏覽所有航程', 'All sailings', 'すべての航路'],
  'menu.water': ['水上設施', 'Water activities', 'ウォーターアクティビティ'],
  'menu.waterSub': ['SUP、水上自行車…', 'SUP, water bikes…', 'SUP・水上自転車など'],
  'menu.ticket': ['查詢票券', 'My ticket', 'チケット照会'],
  'menu.ticketSub': ['我已購票，要報到', 'Already booked — check in', '購入済み・受付へ'],

  'home.recommendTitle': ['我想搭船', 'I want to sail', '船に乗りたい'],
  'home.recommendDesc': [
    '讓小獅船長幫你推薦今天的行程',
    'Let Captain Lion recommend a trip',
    'ライオン船長におすすめを聞く',
  ],
  'home.browseTitle': ['我自己選', 'Browse myself', '自分で選ぶ'],
  'home.browseDesc': [
    '瀏覽所有遊艇／水上活動',
    'Browse all yachts and activities',
    'すべてのヨット・アクティビティ',
  ],
  'home.checkinTitle': ['我已購票，要報到', 'I have a ticket — check in', 'チケット確認・受付'],
  'home.checkinDesc': ['在這裡查詢票券資訊', 'Look up your ticket here', 'チケット情報を確認'],
  'home.voice': ['用講的', 'Voice', '音声'],

  'recommend.title': [
    '讓小獅船長幫你規劃',
    'Let Captain Lion plan for you',
    'ライオン船長がプラン',
  ],
  'recommend.timeQ': ['有多少時間？', 'How much time?', 'どのくらい時間が？'],
  'recommend.time30': ['30 分', '30 min', '30分'],
  'recommend.time60': ['1 小時', '1 hr', '1時間'],
  'recommend.time120': ['2 小時', '2 hr', '2時間'],
  'recommend.time240': ['半天', 'Half day', '半日'],
  'recommend.partyQ': ['幾個人？', 'How many people?', '何名様？'],
  'recommend.interestQ': [
    '想要哪種行程？（可複選）',
    'What kind of trip? (multi-select)',
    'どんな旅？（複数選択可）',
  ],
  // 用語對齊 2026-08-19 設計稿的主功能與行程推薦（原本是「航程／體驗」）
  'cat.trip': ['搭船遊港', 'Harbour cruise', 'ハーバークルーズ'],
  'cat.experience': ['水上體驗', 'Water activity', 'ウォーターアクティビティ'],
  'cat.package': ['套裝', 'Package', 'パッケージ'],
  'cat.charter': ['包船', 'Charter', 'チャーター'],
  'recommend.submit': ['幫我推薦', 'Recommend', 'おすすめを見る'],
  // 設計稿的結果頁標題是「專屬行程」，不是「為你推薦」
  'recommend.resultTitle': ['專屬行程', 'Your itinerary', 'あなた専用のプラン'],
  'recommend.book': ['立即訂購', 'Book now', '今すぐ予約'],
  'recommend.empty': [
    '目前沒有符合的行程，換個條件試試',
    'No matching trips — try other options',
    '該当なし。条件を変えてください',
  ],

  'browse.title': ['選擇航程', 'Choose a voyage', '航海を選ぶ'],
  'browse.titleWater': [
    '選擇水上設施',
    'Choose a water activity',
    'ウォーターアクティビティを選ぶ',
  ],
  'browse.empty': ['目前無可售商品', 'No products available', '販売中の商品はありません'],
  'browse.book': ['立即訂購', 'Book now', '今すぐ予約'],
  'browse.minutes': ['分鐘', 'min', '分'],

  'home.spotsTitle': ['認識安平', 'Discover Anping', '安平を知る'],
  'home.spotsDesc': ['看看附近有什麼景點', 'See nearby attractions', '近くの観光スポットを見る'],
  'spots.title': ['景點介紹', 'Attractions', '観光スポット'],
  'spots.empty': [
    '目前無公開景點資料',
    'No published attractions yet',
    '公開された観光スポットはまだありません',
  ],
  'spots.radius': ['觸發範圍', 'Trigger radius', '通知範囲'],
  'spots.coords': ['座標', 'Coordinates', '座標'],

  // ── 行程推薦（設計稿 p.7）──────────────────────────────────────────
  // 設計稿用的是問句：「想安排多久的旅程呢？」而非「停留時間」。
  'recommend.lead': [
    '小獅船長為您規劃專屬行程',
    'Captain Lion plans your trip',
    '船長があなたの旅を計画します',
  ],
  'recommend.dateQ': ['選擇體驗日期', 'Choose a date', '体験日を選ぶ'],
  'recommend.durationQ': [
    '想安排多久的旅程呢？',
    'How long do you have?',
    'どのくらいの時間がありますか？',
  ],
  'recommend.partySizeQ': [
    '這次有幾位一起出發呢？',
    'How many are travelling?',
    '何名でお出かけですか？',
  ],
  'recommend.experienceQ': [
    '想體驗哪些行程呢？（可複選）',
    'What would you like? (multi-select)',
    'ご希望の体験は？（複数選択可）',
  ],
  'recommend.ask': ['請小獅船長推薦', 'Ask Captain Lion', '船長におすすめを聞く'],
  'recommend.halfDay': ['半天', 'Half day', '半日'],
  'recommend.resultLead': [
    '依照您的選擇，為您安排適合的旅程',
    'Chosen to fit what you told us',
    'ご希望に合わせてご提案します',
  ],
  'recommend.perPerson': ['／人', ' / person', '／名'],
  'recommend.reselect': ['重新選擇', 'Start over', '選び直す'],
  'recommend.orderNow': ['立即訂購', 'Book now', '今すぐ予約'],
  // 兒童年齡區間設計稿標為待確認，先照樣顯示問號，不自行填數字
  'recommend.childRange': ['兒童（?~?歲）', 'Child (?–? yrs)', '子供（?~?歳）'],

  'schedule.title': ['班次與人數', 'Sailing & party size', '便と人数'],
  'schedule.todayLabel': ['今日班次', "Today's sailings", '本日の便'],
  // 清單其實含未來數日，標題不能只說「今日」——一有多天份的班次就會對不上
  'schedule.pickLabel': ['選擇體驗時間', 'Choose your time', '体験時間を選ぶ'],
  'schedule.lead': [
    '選擇出發日期與時段',
    'Pick a date and departure time',
    '出発日と時間帯をお選びください',
  ],
  'schedule.remaining': ['剩餘', 'Left', '残り'],
  'schedule.today': ['今日', 'Today', '本日'],
  'schedule.tomorrow': ['明日', 'Tomorrow', '明日'],
  'schedule.choose': ['選擇', 'Select', '選択'],
  'schedule.selected': ['已選', 'Selected', '選択済み'],
  'schedule.soldOut': ['已售完', 'Sold out', '完売'],
  'schedule.cancelled': ['天候取消', 'Weather cancelled', '天候により欠航'],
  'schedule.partyTitle': ['體驗人數', 'Party size', '体験人数'],
  'schedule.pickFirst': ['請先選擇班次', 'Select a sailing first', '先に便を選択してください'],
  'schedule.adults': ['大人', 'Adults', '大人'],
  'schedule.children': ['兒童', 'Children', '子供'],
  'schedule.subtotal': ['小計', 'Subtotal', '小計'],
  'schedule.originalTotal': ['原價', 'Original', '通常価格'],
  'schedule.promoSaved': ['優惠折抵', 'You save', '割引'],
  'schedule.noSchedule': ['此商品今日無可售班次', 'No sailings today', '本日の便はありません'],
  'schedule.toDelivery': [
    '下一步（取票／發票）',
    'Next (tickets / receipt)',
    '次へ（発券／領収書）',
  ],

  'plan.lead': [
    '請為每一段行程選擇時間',
    'Choose a time for each part of your plan',
    '各体験の時間をお選びください',
  ],
  'plan.segment': ['第 {n} 段', 'Part {n}', '{n} 番目'],
  'plan.pickEach': [
    '請為每一段選擇時間',
    'Choose a time for every part',
    'すべての時間を選択してください',
  ],
  'plan.noSlot': [
    '此段目前沒有可選的時間',
    'No available times for this part',
    'この体験に選べる時間がありません',
  ],
  'plan.clash': ['與已選時段重疊', 'Overlaps a chosen time', '選択済みの時間と重複'],
  'plan.notSellable': [
    '此行程尚未開放購買',
    'This plan is not available yet',
    'このプランは現在ご購入いただけません',
  ],
  'plan.remainingLimit': [
    '本行程最多可購買 {n} 人（受各段名額限制）',
    'Up to {n} people for this plan (limited by each part)',
    'このプランは最大 {n} 名まで（各体験の空き状況による）',
  ],

  'checkout.title': ['取票方式與發票開立', 'Tickets & invoice', '受け取りと領収書'],
  'checkout.lead': [
    '設定完成，領取更方便',
    'Set these up for a smoother pickup',
    '設定しておくと受け取りがスムーズです',
  ],
  'checkout.toPayment': ['下一步（付款方式）', 'Next (payment)', '次へ（お支払い）'],
  'delivery.title': ['體驗票券領取方式', 'How to receive your ticket', 'チケットの受け取り方法'],
  'delivery.paperOut': [
    '印表機缺紙，紙本 QR 暫不可用；請改用 Email / LINE 收票。',
    'Printer out of paper — paper QR unavailable, please use Email or LINE.',
    'プリンタ用紙切れ。紙のQRは利用できません。Email／LINEをお選びください。',
  ],
  'delivery.paperQr': ['紙本 QR', 'Paper QR', '紙のQR'],
  'delivery.paperQrDesc': ['現場列印', 'Printed here', 'その場で印刷'],
  'delivery.email': ['Email', 'Email', 'Email'],
  'delivery.emailDesc': ['寄到信箱', 'Sent to inbox', 'メールで送信'],
  'delivery.line': ['LINE', 'LINE', 'LINE'],
  'delivery.lineDesc': ['推送 QR', 'Pushed via LINE', 'LINEで送信'],
  'delivery.note': [
    '以下方式都會有一張可以掃的 QR Code',
    'Every option gives you a scannable QR code',
    'いずれもスキャンできるQRコードが付きます',
  ],
  'delivery.emailPrompt': ['請輸入 Email', 'Enter your email', 'Emailを入力'],
  'delivery.emailInvalid': [
    'Email 格式不正確',
    'Invalid email format',
    'Emailの形式が正しくありません',
  ],

  'invoice.title': ['發票開立', 'Invoice', '領収書の発行'],
  'invoice.carrier': ['個人', 'Personal', '個人'],
  'invoice.carrierDesc': ['手機條碼載具', 'Mobile barcode', 'モバイルバーコード'],
  'invoice.paper': ['個人', 'Personal', '個人'],
  'invoice.paperDesc': ['一般發票', 'Printed invoice', '紙の領収書'],
  'invoice.donate': ['捐贈', 'Donate', '寄付'],
  'invoice.donateDesc': ['慈善機構', 'To charity', '慈善団体へ'],
  'invoice.company': ['公司', 'Company', '会社'],
  'invoice.companyDesc': ['統編發票', 'With tax ID', '統一番号'],
  'invoice.carrierPrompt': ['條碼（/開頭 8 碼）', 'Barcode (/ + 7 chars)', 'バーコード（/＋7桁）'],
  'invoice.taxIdPrompt': ['統一編號（8 碼）', 'Tax ID (8 digits)', '統一番号（8桁）'],
  'invoice.note': ['發票將於付款後開立', 'Invoice issued after payment', '支払い後に発行されます'],

  'payment.title': ['確認金額並完成付款', 'Confirm and pay', '金額の確認とお支払い'],
  'payment.lead': [
    '選擇好付款方式，旅程準備出發！',
    'Pick how to pay — your trip is ready to go!',
    'お支払い方法を選べば出発の準備完了！',
  ],
  'payment.summaryTitle': ['本次體驗', 'Your experience', '今回の体験'],
  'payment.methodsTitle': ['付款方式', 'Payment method', 'お支払い方法'],
  'payment.totalLabel': ['總金額', 'Total', '合計金額'],
  'payment.departs': ['出發', 'departure', '出発'],
  'payment.creditCard': ['信用卡', 'Credit card', 'クレジットカード'],
  'payment.creditCardDesc': ['IC / 磁條 / 感應', 'IC / Swipe / Contactless', 'IC / 磁気 / タッチ'],
  'payment.applePay': ['Apple Pay', 'Apple Pay', 'Apple Pay'],
  'payment.googlePay': ['Google Pay', 'Google Pay', 'Google Pay'],
  'payment.linePay': ['LINE Pay', 'LINE Pay', 'LINE Pay'],
  'payment.note': [
    '選擇後請依刷卡機指示操作',
    'Follow the card reader prompts',
    'カードリーダーの指示に従ってください',
  ],
  'payment.pay': [
    '確認付款（領取票券）',
    'Confirm payment (get tickets)',
    'お支払い確定（チケット受取）',
  ],

  'paying.title': [
    '正在處理付款，請依刷卡機指示',
    'Processing payment — follow the reader',
    '決済処理中 — リーダーの指示に従ってください',
  ],
  'paying.authorizing': ['授權中…', 'Authorizing…', '承認中…'],
  'paying.doNotLeave': [
    '請勿移動或關閉螢幕',
    'Please do not leave or close the screen',
    '画面を閉じないでください',
  ],
  'paying.orderNo': ['訂單號', 'Order no.', '注文番号'],
  'paying.note': [
    '付款成功才會發行票券',
    'Tickets issue only on successful payment',
    '決済成功後にチケット発行',
  ],
  'paying.failedTitle': ['付款未完成', 'Payment not completed', '決済が完了しませんでした'],
  'paying.failedDesc': [
    '未授權成功的訂單不會扣款。',
    'Unauthorized orders are not charged.',
    '未承認の注文に課金はありません。',
  ],
  'paying.oversoldTitle': ['座位剛好售出', 'Seat just sold out', '座席が完売しました'],
  'paying.oversoldDesc': [
    '款項將自動退回原付款方式，無須擔心。',
    'Your payment will be auto-refunded.',
    '自動的に返金されます。',
  ],
  'paying.retry': ['重新付款', 'Pay again', 'もう一度支払う'],

  'done.title': ['購票完成！', 'Booking complete!', '購入完了！'],
  'done.delivering': ['票券正在送達', 'Your ticket is on the way', 'チケットを送信中'],
  'done.paperPrinting': ['紙本 QR Code 印出中…', 'Printing paper QR…', '紙のQRを印刷中…'],
  'done.emailSent': ['票券已寄出 Email', 'Ticket emailed', 'チケットをメール送信'],
  'done.lineSent': [
    '加 LINE 好友後 30 秒內收到 QR',
    'Add LINE friend to get the QR',
    'LINE友だち追加でQR受信',
  ],
  'done.boardingQr': ['登船 QR', 'Boarding QR', '乗船QR'],
  'done.keepQr': [
    '請保留 QR，登船時掃碼',
    'Keep the QR — scan it when boarding',
    'QRを保管し乗船時にスキャン',
  ],
  'done.finish': ['完成', 'Done', '完了'],

  'checkin.title': ['報到 — 請出示 QR Code', 'Check-in — show your QR', '受付 — QRコードを提示'],
  'checkin.guideLine1': [
    '請至登船口由現場人員協助掃碼。',
    'Please proceed to the boarding gate; staff will scan your QR.',
    '乗船口でスタッフがQRを読み取ります。',
  ],
  'checkin.guideLine2': [
    '購票機僅供查詢，實際報到由現場人員完成。',
    'This kiosk is for lookup only; actual check-in is done by staff.',
    'こちらは確認用です。受付は現地スタッフが行います。',
  ],
  'checkin.scanArea': [
    '掃碼區（請至登船口）',
    'Scan area (at boarding gate)',
    'スキャンエリア（乗船口）',
  ],
  'checkin.helpTitle': ['需要幫忙嗎？', 'Need help?', 'お困りですか？'],
  'checkin.help1': ['找不到票券', "Can't find my ticket", 'チケットが見つからない'],
  'checkin.help2': ['QR 掃不到', "QR won't scan", 'QRが読み取れない'],
  'checkin.help3': ['沒收到票', "Didn't receive my ticket", 'チケットが届いていない'],
  'checkin.staffPrompt': [
    '請按「呼叫服務人員」鈴鐺，現場人員會立刻協助。',
    'Press the bell to call staff for immediate help.',
    'ベルを押すとスタッフがすぐに対応します。',
  ],
  'checkin.supervisor': ['督導例外通道', 'Supervisor override', 'スーパーバイザー処理'],

  'supervisor.modalTitle': ['督導例外通道', 'Supervisor override', 'スーパーバイザー処理'],
  'supervisor.warning': [
    '⚠️ 此通道為高責任放行，每筆稽核',
    'High-responsibility override — fully audited',
    '高責任モード — すべて監査対象',
  ],
  'supervisor.reasonLabel': ['原因（必填）', 'Reason (required)', '理由（必須）'],
  'supervisor.reasonPlaceholder': [
    '例：顧客已上船待處理',
    'e.g. passenger boarded pending review',
    '例：乗船済み・要対応',
  ],
  'supervisor.pinLabel': ['主管 PIN', 'Supervisor PIN', 'スーパーバイザーPIN'],
  'supervisor.submit': ['確認放行', 'Confirm override', '承認する'],
  'supervisor.stub': [
    'ⓘ 督導通道後端串接待 D-2 manual_override API。已記錄此次嘗試。',
    'ⓘ Supervisor backend (D-2 manual_override) is pending. Attempt logged.',
    'ⓘ 後端API（D-2）未接続。試行は記録済み。',
  ],

  'voice.title': ['小獅船長能幫你', 'Captain Lion can help', 'ライオン船長がサポート'],
  'voice.unavailable': [
    'ⓘ 語音對話即將開放，現在可以從常見問題挑一個',
    'ⓘ Voice chat coming soon — pick a common question for now',
    'ⓘ 音声対話は近日公開。よくある質問からお選びください',
  ],
  'voice.q1': ['下一班船幾點？', 'When is the next sailing?', '次の便は何時？'],
  'voice.q2': ['推薦今天的行程', "Recommend today's trip", '本日のおすすめ'],
  'voice.q3': ['我自己選航程', 'Browse all voyages', 'すべての航海'],
  'voice.q4': ['我已購票，要報到', 'I have a ticket — check in', 'チケット確認・受付'],
  'voice.contractNote': [
    'ⓘ 完整語音對話需 C-3 LLM 廠商簽約完成後上線。',
    'ⓘ Full voice conversation activates after C-3 vendor contract.',
    'ⓘ 完全な音声対話はC-3ベンダー契約後に有効化。',
  ],
};

/**
 * 取譯字串。`{name}` 佔位符以 params 內插。缺鍵時回 key 本身（dev 可見）。
 */
export function translate(
  locale: Locale,
  key: string,
  params?: Record<string, string | number>,
): string {
  const entry = STRINGS[key];
  if (!entry) return key;
  const idx = locale === 'zh-TW' ? 0 : locale === 'en' ? 1 : 2;
  let out = entry[idx] || entry[0];
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      out = out.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
    }
  }
  return out;
}
